import React, { useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'
import InfiniteScroll from 'react-infinite-scroll-component'
import moment from 'moment'

import {
  Card,
  Row,
  Text,
  Grid,
  Line,
  Span,
  CollapseColumn,
  TableBodyStyled,
  TableCellStyled,
  TableContainerStyled,
  TableHeadStyled,
  TableRowStyled,
  TableStyled,
  Button,
  PageFooter,
  Flex
} from 'components'
import { ExcelIcon, PdfIcon } from 'components/Svg'
import { useLoading, useModal, useException, useAuth } from 'hooks'
import useApp from 'hooks/useApp'
import {
  IResponseEndResult,
  IResponseResultGroups,
  IResponseResultGroupsItems,
  ReportsCREServices,
  IRequestCards,
  IResponseEndResultEntity
} from 'services/NGT/ReportsCRE'
import { COLORS, SPACING, FONTS, TYPOGRAPHY, FORMAT } from 'utils'

import TableReportCRE from '../TableReportCRE'
import { Pages } from '@mui/icons-material'
import LoadingIcon from 'components/Icons/LoadingIcon/LoadingIcon'
import { AxiosResponse } from 'axios'

interface IState extends IResponseEndResult {
  action?: string
  allFields?: IRequestCards
  data?: IResponseEndResult
}

export const ResultsReportsCRE: React.FC = () => {
  const { goBack } = useHistory()
  const { setTitlePage } = useApp()

  const { state } = useLocation<IState>()

  const { getException, createCustomException } = useException()

  const { setLoading, setMsgErro } = useLoading()
  const { showModal } = useModal()
  const [loadingPDF, setLoadingPDF] = useState<boolean>(false)
  const [loadingExcel, setLoadingExcel] = useState<boolean>(false)
  const [textLoadingPDF, setTextLoadingPDF] = useState<string>('')
  const [textLoadingExcel, setTextLoadingExcel] = useState<string>('')
  // Paginação
  const [isFirstGet, setIsFirstGet] = useState<boolean>(false)
  const [reportCREData, setReportCREData] = useState<IResponseEndResultEntity>()
  const [reportCREGrupos, setReportCREGrupos] = useState<
    IResponseResultGroups[]
  >([])
  const [page, setPage] = useState<number>(1)
  const [totalItensPaginated, setTotalItensPaginated] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(15)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const { user } = useAuth()

  // const notCollapsedByDefault = reportCREData.grupos?.map(() => false)
  // const [collapsesState, setCollapsesState] = useState(notCollapsedByDefault)

  const getReportData = () => {
    if (page == 1) {
      setLoading('loading')
    }

    if (page > 1) {
      if (totalItensPaginated <= reportCREGrupos.length) {
        setHasMore(false)
        setLoading('idle')
        return
      }
    }

    ReportsCREServices(user)
      .getEndResult({
        ...state.allFields,
        pageNumber: page,
        flPaginacao: !isFirstGet,
        pageSize: pageSize
      })
      .then((response) => {
        if (response?.status !== 200 || !response?.data.sucesso) {
          createCustomException({
            message:
              response.data.mensagem ||
              'O contrato ou o fornecedor selecionado não tem pendência até o presente momento que esteja em aberto.'
          })
        }

        setReportCREData(response.data.entity)

        if (response.data.entity.grupos.length) {
          setReportCREGrupos((prevState) =>
            prevState.concat(response.data.entity.grupos)
          )
        }
        if (page === 1) {
          setTotalItensPaginated(response.data.entity.totalItensPaginated)
        }

        setPage(page + 1)
        setLoading('idle')
      })
      .catch((err: Error) => {
        const error = getException(err)
        setLoading('idle')
        showModal({
          handleConfirm: () => goBack(),
          hideButtonCancel: true,
          message: error.message,
          txtButtonConfirm: 'Fechar',
          type: 'alert'
        })
      })
  }

  useEffect(() => {
    if (state?.allFields === null || state?.data === undefined) {
      return goBack()
    }

    getReportData()

    return () => {
      setTitlePage(null)
    }
  }, [])

  setTitlePage(reportCREData?.titulo || null)

  const formatedPageTitle =
    reportCREData?.titulo
      ?.replaceAll(' ', '-')
      ?.replaceAll('--', '')
      ?.replaceAll('---', '')
      ?.toLowerCase() || 'relatorio-cre'

  const dateNow = moment().format('DD-MM-YYYY')
  const timeNow = moment().format('HH-mm-ss')
  const formatTime = timeNow.replace('-', 'h').replace('-', 'm').concat('s')
  const dateAndTimeNow = `-${dateNow}-${formatTime}`
  const fileName = `${formatedPageTitle}${dateAndTimeNow}`

  const handleExcel = () => {
    const { action, data: dataState } = state
    setLoadingExcel(true)
    setTextLoadingExcel('Começamos a gerar o seu Excel, Aguarde um momento.')
    ReportsCREServices(user)
      .getExcelResult({
        ...state?.allFields,
        pageNumber: 1,
        flPaginacao: !isFirstGet,
        pageSize: totalItensPaginated
      })
      .then((response) => {
        const url =
          'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' +
          response.data
        const downloadLink = document.createElement('a')
        downloadLink.href = url
        downloadLink.download = `${fileName}.xlsx`
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)

        setLoadingExcel(false)
        setTextLoadingExcel('')
      })
      .catch((err: Error) => {
        const error = getException(err)
        setLoadingExcel(false)
        setTextLoadingExcel('')
        showModal({
          hideButtonCancel: true,
          message: 'Ocorreu um problema ao gerar Excel.',
          txtButtonConfirm: 'Fechar',
          type: 'alert'
        })
      })
      .finally(() => {
        setLoadingExcel(false)
        setTextLoadingExcel('')
      })
  }

  const handlePrint = () => {
    setLoadingPDF(true)
    setTextLoadingPDF(`Começamos a gerar seu PDF, Aguarde um momento.`)
    const flDocPostado = state?.allFields?.flDocPostado ?? null;
    ReportsCREServices(user)
      .getPDFResult({
        ...state?.allFields,
        flPaginacao: !isFirstGet,
        flDocPostado: flDocPostado,
        pageNumber: 1,
        pageSize: totalItensPaginated
      })
      .then((response) => {
        if (response?.status !== 200) {
          createCustomException({
            message: 'Ocorreu um erro ao gerar o relatório'
          })
          setTextLoadingPDF('')
          setLoadingPDF(false)
        }

        if (response.data.length < 67) {
          showModal({
            hideButtonCancel: true,
            message: response.data,
            txtButtonConfirm: 'Fechar',
            type: 'success'
          })
          setTextLoadingPDF('')
          setLoadingPDF(false)
          return
        }

        const url = 'data:application/pdf;base64,' + response.data
        const downloadLink = document.createElement('a')
        downloadLink.href = url
        downloadLink.download = `${fileName}.pdf`
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)

        setTextLoadingPDF('')
        setLoadingPDF(false)
      })
      .catch((error) => {
        showModal({
          hideButtonCancel: true,
          message: 'Ocorreu um problema ao gerar PDF.',
          txtButtonConfirm: 'Fechar',
          type: 'alert'
        })
        setLoadingPDF(false)
        setTextLoadingPDF('')
      })
  }

  return !reportCREData ? (
    <>
      <Row
        justifyContent="flex-end"
        marginTop={SPACING.spacing30}
        marginRight={SPACING.spacing30}
      >
        <Button
          text="Voltar"
          variantButton="outlined"
          onClick={() => goBack()}
        />
      </Row>
    </>
  ) : (
    <>
      <Row
        alignItems="center"
        justifyContent="end"
        marginTop={SPACING.spacing15}
        marginBottom={SPACING.spacing15}
      >
        <Row
          alignItems="center"
          marginTop={SPACING.spacing20}
          marginLeft={SPACING.spacing20}
          marginRight={SPACING.spacing20}
        >
          {loadingPDF ? (
            <>
              <Grid justifyItems="center" gridCols="1fr" gridGap="1rem">
                <LoadingIcon colorIcon={COLORS.blueFonts} />
                <Span text={`${textLoadingPDF}`} />
              </Grid>
            </>
          ) : (
            <PdfIcon onClick={handlePrint} />
          )}
          {loadingExcel ? (
            <>
              <Grid justifyItems="center" gridCols="1fr" gridGap="1rem">
                <LoadingIcon colorIcon={COLORS.blueFonts} />
                <Span text={`${textLoadingExcel}`} />
              </Grid>
            </>
          ) : (
            <ExcelIcon style={{ marginLeft: `{SPACING.spacing15}` }} onClick={handleExcel} />
          )}
          <Button
            text="Voltar"
            variantButton="outlined"
            onClick={() => goBack()}
          />
        </Row>
      </Row>

      <Card
        // id="pdfView"
        marginTop={SPACING.spacing20}
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0 0 1em ${COLORS.lightGray}`}
      >
        <Span
          fontSize={TYPOGRAPHY.subtitleSize20}
          text={reportCREData?.subTitulo || ''}
        />
        <InfiniteScroll
          dataLength={reportCREGrupos.length}
          next={getReportData}
          hasMore={hasMore}
          loader={
            <Box sx={{ width: '100%', marginBottom: '20px' }}>
              <LinearProgress />
            </Box>
          }
          endMessage={
            <p style={{ textAlign: 'center' }}>
              <b>Final da listagem</b>
            </p>
          }
          releaseToRefreshContent={
            <h3 style={{ textAlign: 'center' }}>&#8593;Atualizando...</h3>
          }
        >
          {reportCREGrupos?.map((contract, index) => {
            return (
              <CollapseColumn
                key={index.toString()}
                index={index}
                inicio={FORMAT.formatDDMMYYYY(contract.cabecalho.dtContratoIni)}
                fim={FORMAT.formatDDMMYYYY(contract.cabecalho.dtContratoFim)}
                nuContrato={contract.cabecalho.cdContrato}
                nmFornecedor={contract.cabecalho.nmFornecedor}
                nmSubContratada={contract.cabecalho.nmSubContratada}
                nmDiretoriaExecutiva={contract.cabecalho.de}
                nmDiretoria={contract.cabecalho.dir}
                nmGerenciaGeral={contract.cabecalho.gg}
                entradaDia={FORMAT.formatDDMMYYYY(contract.cabecalho.dtEntrada)}
                retornoDia={FORMAT.formatDDMMYYYY(contract.cabecalho.dtRetorno)}
                nmGerencia={contract.cabecalho.ger}
                cre={contract.cabecalho.cdCRE}
                //dtVerificacao={contract.cabecalho.dtVerificacao}
                analista={contract.cabecalho.nmAnalista}
                matricula={contract.cabecalho.cdMatricula}
                diasBloqueio={contract.cabecalho.qtDiasBloqueio}
                efetivo={contract.cabecalho.qtTotalEfetivos}
                // isOpen={collapsesState[index] || false}
                isOpen={true}
                // toggleExpand={handleToggleCollapse}
                toggleExpand={() => null} // Regra inserta, se não colocar null o componente quebra.
              >
                <Row alignItems="center" justifyContent="center">
                  <Span
                    text="DESCRIÇÃO DAS PENDÊNCIAS"
                    textAlign="center"
                    color={COLORS.blueFonts}
                    fontSize="24px"
                    fontWeight="700"
                  />
                </Row>
                <TableReportCRE tableData={contract.itens} />
              </CollapseColumn>
            )
          })}
        </InfiniteScroll>
        <Line background={COLORS.blueFonts} />

        <PageFooter>
          <Grid
            justifyItems="center"
            gridCols="1fr 1fr 1fr 1fr 1fr "
            gridGap="1rem"
            margin="1rem 0 1rem 0"
          >
            <Text>
              <Span fontSize={TYPOGRAPHY.bodySize14} text="Total Pendências " />
              <Span
                fontSize={TYPOGRAPHY.bodySize14}
                fontWeight={FONTS.bold}
                text={`T : ${state?.data?.entity?.totalTrabalhista?.qtPendencias}`}
              />
            </Text>
            <Text>
              <Span fontSize={TYPOGRAPHY.bodySize14} text="Total Risco " />
              <Span
                fontSize={TYPOGRAPHY.bodySize14}
                fontWeight={FONTS.bold}
                text="T : "
              />
            </Text>
            <Span
              fontSize={TYPOGRAPHY.bodySize14}
              fontWeight={FONTS.bold}
              text={FORMAT.currency(
                state?.data?.entity?.totalTrabalhista?.vlRisco
              )}
            />
            <Text>
              <Span
                fontSize={TYPOGRAPHY.bodySize14}
                text="Total Risco Evitado "
              />
              <Span
                fontSize={TYPOGRAPHY.bodySize14}
                fontWeight={FONTS.bold}
                text="T :"
              />
            </Text>
            <Span
              fontSize={TYPOGRAPHY.bodySize14}
              fontWeight={FONTS.bold}
              text={FORMAT.currency(
                state?.data?.entity?.totalTrabalhista?.vlRiscoEvitado
              )}
            />
            <Text>
              <Span fontSize={TYPOGRAPHY.bodySize14} text="Total Pendências " />
              <Span
                fontSize={TYPOGRAPHY.bodySize14}
                fontWeight={FONTS.bold}
                text={`P : ${state?.data?.entity?.totalPrevidenciario?.qtPendencias}`}
              />
            </Text>
            <Text>
              <Span fontSize={TYPOGRAPHY.bodySize14} text="Total Risco " />
              <Span
                fontSize={TYPOGRAPHY.bodySize14}
                fontWeight={FONTS.bold}
                text="P : "
              />
            </Text>
            <Span
              fontSize={TYPOGRAPHY.bodySize14}
              fontWeight={FONTS.bold}
              text={FORMAT.currency(
                state?.data?.entity?.totalPrevidenciario?.vlRisco
              )}
            />
            <Text>
              <Span
                fontSize={TYPOGRAPHY.bodySize14}
                text="Total Risco Evitado "
              />
              <Span
                fontSize={TYPOGRAPHY.bodySize14}
                fontWeight={FONTS.bold}
                text="P :"
              />
            </Text>
            <Span
              fontSize={TYPOGRAPHY.bodySize14}
              fontWeight={FONTS.bold}
              text={FORMAT.currency(
                state?.data?.entity?.totalPrevidenciario?.vlRiscoEvitado
              )}
            />
            <Text>
              <Span fontSize={TYPOGRAPHY.bodySize14} text="Total Pendências " />
              <Span
                fontSize={TYPOGRAPHY.bodySize14}
                fontWeight={FONTS.bold}
                text={`O : ${state?.data?.entity?.totalOutros?.qtPendencias}`}
              />
            </Text>
            <Text>
              <Span fontSize={TYPOGRAPHY.bodySize14} text="Total Risco " />
              <Span
                fontSize={TYPOGRAPHY.bodySize14}
                fontWeight={FONTS.bold}
                text="O : "
              />
            </Text>
            <Span
              fontSize={TYPOGRAPHY.bodySize14}
              fontWeight={FONTS.bold}
              text={FORMAT.currency(state?.data?.entity?.totalOutros?.vlRisco)}
            />
            <Text>
              <Span
                fontSize={TYPOGRAPHY.bodySize14}
                text="Total Risco Evitado "
              />
              <Span
                fontSize={TYPOGRAPHY.bodySize14}
                fontWeight={FONTS.bold}
                text="O:"
              />
            </Text>
            <Span
              fontSize={TYPOGRAPHY.bodySize14}
              fontWeight={FONTS.bold}
              text={FORMAT.currency(
                state?.data?.entity?.totalOutros?.vlRiscoEvitado
              )}
            />
          </Grid>
          <Grid>
            <TableContainerStyled>
              <TableStyled>
                <TableHeadStyled>
                  <TableRowStyled>
                    <TableCellStyled align="center">
                      Total Geral Pendências:{' '}
                      {state?.data?.entity.totalGeral.qtPendencias}
                    </TableCellStyled>
                    <TableCellStyled align="center">
                      Total Geral Bloqueada:{' '}
                      {state?.data?.entity.totalGeral.qtPendencias -
                        state?.data?.entity.totalGeral.qtPendenciasResolvidas}
                    </TableCellStyled>
                    <TableCellStyled align="center">
                      Total Pendências Liberada:{' '}
                      {state?.data?.entity.totalGeral.qtPendenciasResolvidas}
                    </TableCellStyled>
                    <TableCellStyled align="center">
                      Total Geral Risco:{' '}
                      {FORMAT.currency(state?.data?.entity.totalGeral.vlRisco)}
                    </TableCellStyled>
                    <TableCellStyled align="center">
                      Total Geral Riscos Evitados:{' '}
                      {FORMAT.currency(
                        state?.data?.entity.totalGeral.vlRiscoEvitado
                      )}
                    </TableCellStyled>
                  </TableRowStyled>
                </TableHeadStyled>
                <TableBodyStyled>
                  <TableRowStyled style={{ background: COLORS.lightGray }}>
                    <TableCellStyled align="center"></TableCellStyled>
                    <TableCellStyled align="center"></TableCellStyled>
                    <TableCellStyled align="center"></TableCellStyled>
                    <TableCellStyled align="center"></TableCellStyled>
                  </TableRowStyled>
                </TableBodyStyled>
              </TableStyled>
            </TableContainerStyled>
          </Grid>
          <Line background={COLORS.blueFonts} />
          <Row justifyContent="flex-end" marginTop={SPACING.spacing20}></Row>
        </PageFooter>
      </Card>
    </>
  )
}
